import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import theme from '../theme/theme';
import axios from 'axios';

// Get API URL (handled in Nginx reverse proxy, usually it goes to /api)
const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export interface SystemSettings {
  institution_name: string;
  institution_type: string;
  logo: string | null;
  brand_color_primary: string;
  brand_color_secondary: string;
  contact_email: string;
  contact_phone: string;
}

interface SettingsContextType {
  settings: SystemSettings;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const defaultSettings: SystemSettings = {
  institution_name: 'CreaSync Academy',
  institution_type: 'university',
  logo: null,
  brand_color_primary: '#0A2540',
  brand_color_secondary: '#0070E0',
  contact_email: 'soporte@creasync.local',
  contact_phone: '',
};

const SettingsContext = createContext<SettingsContextType>({
  settings: defaultSettings,
  loading: true,
  refreshSettings: async () => {},
});

export const useSystemSettings = () => useContext(SettingsContext);

export const DynamicThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTheme, setCurrentTheme] = useState(theme);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/core/settings/`);
      if (response.data) {
        const fetchedData = response.data;
        setSettings({
          institution_name: fetchedData.institution_name || defaultSettings.institution_name,
          institution_type: fetchedData.institution_type || defaultSettings.institution_type,
          logo: fetchedData.logo || defaultSettings.logo,
          brand_color_primary: fetchedData.brand_color_primary || defaultSettings.brand_color_primary,
          brand_color_secondary: fetchedData.brand_color_secondary || defaultSettings.brand_color_secondary,
          contact_email: fetchedData.contact_email || defaultSettings.contact_email,
          contact_phone: fetchedData.contact_phone || defaultSettings.contact_phone,
        });

        // Dynamically override primary/secondary colors in MUI theme based on dynamic parameters
        const customTheme = createTheme({
          ...theme,
          palette: {
            ...theme.palette,
            primary: {
              ...theme.palette.primary,
              main: fetchedData.brand_color_primary || '#0A2540',
            },
            secondary: {
              ...theme.palette.secondary,
              main: fetchedData.brand_color_secondary || '#0070E0',
            },
          },
        });
        setCurrentTheme(customTheme);

        // Update document title dynamically
        if (fetchedData.institution_name) {
          document.title = fetchedData.institution_name;
        }
      }
    } catch (error) {
      logger.error('Failed to load system settings, falling back to defaults:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings: fetchSettings }}>
      <ThemeProvider theme={currentTheme}>
        {children}
      </ThemeProvider>
    </SettingsContext.Provider>
  );
};

// Simple logger fallback inside frontend
const logger = {
  error: (...args: any[]) => console.error('[SettingsProvider]', ...args),
};
